/**
 * Load 계층: scroll sentinel과 keyboard prefetch가 공유하는 중복 억제 onLoadMore 경계.
 */
import { useCallback, useEffect, useRef, type RefObject } from "react";

// 마지막 (1-threshold) 구간이 시작되는 인덱스. 이 인덱스가 보이거나 선택되면 다음 페이지를 당긴다.
export function prefetchIndex(loadedCount: number, threshold: number): number {
  return Math.floor(loadedCount * threshold);
}

export function useListViewLoadBoundary({
  itemCount,
  loadedCount,
  hasMore,
  loadingMore,
  onLoadMore,
  prefetchThreshold,
  scrollerRef,
}: {
  itemCount: number;
  /** prefetch 인덱스의 기준. 첫 페이지 로드 전에는 0이다. */
  loadedCount: number;
  hasMore: boolean;
  loadingMore: boolean;
  onLoadMore: (() => void) | undefined;
  prefetchThreshold: number;
  scrollerRef: RefObject<HTMLDivElement | null>;
}): {
  triggerRef: RefObject<HTMLDivElement | null>;
  requestLoadMore: () => void;
  triggerIndex: number;
} {
  const triggerRef = useRef<HTMLDivElement>(null);
  const loadRequestPendingRef = useRef(false);
  const previousItemsLengthRef = useRef(itemCount);
  const sawLoadingMoreRef = useRef(loadingMore);

  const requestLoadMore = useCallback(() => {
    if (loadRequestPendingRef.current || !hasMore || loadingMore || onLoadMore === undefined) {
      return;
    }
    loadRequestPendingRef.current = true;
    try {
      onLoadMore();
    } catch (error) {
      loadRequestPendingRef.current = false;
      throw error;
    }
  }, [hasMore, loadingMore, onLoadMore]);

  // append, 완료, 실패 후 loading 해제 중 하나가 확인될 때만 다음 요청 latch를 연다.
  useEffect(() => {
    if (itemCount > previousItemsLengthRef.current) {
      loadRequestPendingRef.current = false;
    }
    previousItemsLengthRef.current = itemCount;
    if (!hasMore) loadRequestPendingRef.current = false;
    if (loadingMore) {
      sawLoadingMoreRef.current = true;
    } else if (sawLoadingMoreRef.current) {
      sawLoadingMoreRef.current = false;
      loadRequestPendingRef.current = false;
    }
  }, [hasMore, itemCount, loadingMore]);

  // bottom sentinel이 rootMargin 안으로 들어오면 scroll/keyboard 공용 latch로 요청한다.
  useEffect(() => {
    if (!hasMore || !onLoadMore || loadingMore) return;
    if (typeof IntersectionObserver === "undefined") return;
    const trigger = triggerRef.current;
    const root = scrollerRef.current;
    if (!trigger || !root) return;
    const clamped = Math.min(1, Math.max(0, prefetchThreshold));
    const bottomMarginPct = Math.round((1 - clamped) * 100);
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) requestLoadMore();
      },
      { root, rootMargin: `0px 0px ${bottomMarginPct}% 0px` },
    );
    observer.observe(trigger);
    return () => observer.disconnect();
  }, [
    hasMore,
    itemCount,
    loadingMore,
    onLoadMore,
    prefetchThreshold,
    requestLoadMore,
    scrollerRef,
  ]);

  return {
    triggerRef,
    requestLoadMore,
    triggerIndex: prefetchIndex(loadedCount, prefetchThreshold),
  };
}
